import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import { agentStatusService, AgentStatus } from '@/api/services/agentStatus.service';

interface AgentStatusesState {
  statuses: AgentStatus[];
  isSubmitting: boolean;
  error: string | null;
  isLoading: boolean;
  userCurrentStatus: string | null;
}

const initialState: AgentStatusesState = {
  statuses: [],
  isSubmitting: false,
  error: null,
  isLoading: false,
  userCurrentStatus: null,
};

// Async thunks
export const fetchAgentStatuses = createAsyncThunk(
  'agentStatuses/fetchStatuses',
  async (_, { rejectWithValue }) => {
    try {
      return await agentStatusService.getAgentStatuses();
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message ?? 'Failed to fetch Agent statuses');
    }
  }
);

export const createAgentStatus = createAsyncThunk(
  'agentStatuses/create',
  async (payload: Partial<AgentStatus>, { rejectWithValue }) => {
    try {
      return await agentStatusService.createAgentStatus(payload);
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message ?? 'Failed to create Agent status');
    }
  }
);

export const updateAgentStatus = createAsyncThunk(
  'agentStatuses/update',
  async ({ id, data }: { id: string; data: Partial<AgentStatus> }, { rejectWithValue }) => {
    try {
      return await agentStatusService.updateAgentStatus(id, data);
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message ?? 'Failed to update Agent status');
    }
  }
);

export const deleteAgentStatus = createAsyncThunk(
  'agentStatuses/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await agentStatusService.deleteAgentStatus(id);
      return id;
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message ?? 'Failed to delete Agent status');
    }
  }
);

export const getUserAgentStatus = createAsyncThunk(
  'agentStatuses/getUserStatus',
  async (_, { rejectWithValue }) => {
    try {
      return await agentStatusService.getUserAgentStatuses();
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message ?? 'Failed to fetch User Agent statuses');
    }
  }
);

export const updateUserAgentStatus = createAsyncThunk(
  'agentStatuses/updateUserStatus',
  async (payload: { agentStatusId: string }, { rejectWithValue }) => {
    try {
      return await agentStatusService.updateUserAgentStatus(payload);
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message ?? 'Failed to update User Agent status');
    }
  }
);

export const keepUserActive = createAsyncThunk(
  'agentStatuses/keepUserActive',
  async (_, { rejectWithValue }) => {
    try {
      return await agentStatusService.updateUserLastActivityTimestamp();
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message ?? 'Failed to keep user active');
    }
  }
);

const agentStatusesSlice = createSlice({
  name: 'agentStatuses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch statuses
      .addCase(fetchAgentStatuses.pending, (state) => {
        console.log('fetchAgentStatuses.pending');
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgentStatuses.fulfilled, (state, action) => {
        console.log('fetchAgentStatuses.fulfilled', action.payload);
        state.isLoading = false;
        state.statuses = action.payload;
      })
      .addCase(fetchAgentStatuses.rejected, (state, action) => {
        console.log('fetchAgentStatuses.rejected', action.error);
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch statuses';
      })

      // Create status
      .addCase(createAgentStatus.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(createAgentStatus.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.statuses.push(action.payload);
      })
      .addCase(createAgentStatus.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'Failed to create status';
      })

      // Update status
      .addCase(updateAgentStatus.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(updateAgentStatus.fulfilled, (state, action) => {
        state.isSubmitting = false;

        const index = state.statuses.findIndex(status => status.id === action.payload.id);
        if (index !== -1) {
          state.statuses[index] = action.payload;
        }
      })
      .addCase(updateAgentStatus.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'Failed to update status';
      })

      // Delete status
      .addCase(deleteAgentStatus.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(deleteAgentStatus.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.statuses = state.statuses.filter(status => status.id !== action.payload);
      })
      .addCase(deleteAgentStatus.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'Failed to delete status';
      })

      // Get user current status
      .addCase(getUserAgentStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserAgentStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userCurrentStatus = action.payload.agentStatusId;
      })
      .addCase(getUserAgentStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch user current status';
      })

      // Update user status
      .addCase(updateUserAgentStatus.pending, (state) => {
        state.isLoading = true;
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(updateUserAgentStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSubmitting = false;
        state.userCurrentStatus = action.payload;
      })
      .addCase(updateUserAgentStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.isSubmitting = false;
        state.error = action.error.message || 'Failed to update user current status';
      })
  }
});

export const { clearError } = agentStatusesSlice.actions;

// Selectors
export const selectAgentStatuses = (state: RootState) => state.agentStatuses?.statuses || [];
export const selectDefaultStatuses = (state: RootState) =>
  state.agentStatuses?.statuses?.filter(status => status.isSystemDefaultStatus) || [];
export const selectCustomStatuses = (state: RootState) =>
  state.agentStatuses?.statuses?.filter(status => !status.isSystemDefaultStatus) || [];
export const selectIsSubmitting = (state: RootState) => state.agentStatuses?.isSubmitting || false;
export const selectAgentStatusesError = (state: RootState) => state.agentStatuses?.error || null;
export const selectIsLoading = (state: RootState) => state.agentStatuses?.isLoading || false;
export const selectUserCurrentStatus = (state: RootState) => state.agentStatuses?.userCurrentStatus || null;

export default agentStatusesSlice.reducer;